<template>
  <div>
    <dx-grid
      :url="url"
      :columns="columns"
      :title="title"
      :beforeSetupGrid="beforeSetupGrid"
      :actions="actions"
      ref="grid"
      :onRowClicked="onRowClicked"
      navigationPath="onRowClicked"
      navigationField="onRowClicked"
    ></dx-grid>

    <v-dialog persistent v-model="importDialog" max-width="450">
      <v-card class="pa-3">
        <v-card-title class="headline pl-0 pt-0 pb-2">Import DOE</v-card-title>
        <v-layout row>
          <v-btn
            small
            outline
            class="mr-2"
            :loading="uploading"
            @click="pickFile()"
          >
            Import
            <v-icon right dark>mdi-cloud-upload</v-icon>
          </v-btn>
          <div v-if="files.length" class="pt-2 mt-2 ml-2">
            {{ files[0].name }}
          </div></v-layout
        >
        <input
          type="file"
          style="display: none"
          ref="files"
          accept=".csv"
          @change="onFilePicked($event)"
        />
        <div class="mt-2 mb-1 mx-1">
          Enter a list of Zip Codes's separated by a comma
        </div>
        <v-textarea
          class="mx-1 mt-0"
          label="Zipcodes"
          v-model="zipcodes"
          :rules="[rules.zipcode]"
          outline
          rows="2"
          ref="zipcodes"
        ></v-textarea>
        <v-card-actions class="mr-0 mt-3">
          <v-spacer></v-spacer>
          <v-btn color="default" dark @click="closeImportDialog()"
            >Cancel</v-btn
          >
          <v-btn
            :disabled="!files.length"
            :loading="processing"
            color="success"
            @click="importFile()"
          >
            import
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog persistent v-model="votingDialog" max-width="1000">
      <voting-master-list-person
        :person="presonInfo"
        :reload="reload"
        :close="closeVotingDialog"
      ></voting-master-list-person>
    </v-dialog>

    <v-dialog persistent v-model="exportDialog" max-width="480">
      <v-card class="export-dialog-card">
        <div class="export-dialog-header">
          <v-icon large color="white" class="mr-3"
            >mdi-file-export-outline</v-icon
          >
          <span class="export-dialog-title">Export DOE Data</span>
        </div>

        <v-card-text class="pt-4 pb-2 px-4">
          <!-- ── Idle: waiting for user to click Start ── -->
          <div
            v-if="!exportRunning && !exportCancelled && !exportDone"
            class="export-idle-state"
          >
            <div class="export-idle-icon-wrap mb-3">
              <v-icon color="success" size="46">mdi-file-export</v-icon>
            </div>
            <div class="export-idle-title mb-2">Ready to export</div>
            <div class="export-idle-subtitle grey--text mb-4">
              {{ exportTotal.toLocaleString() }} records will be fetched in
              batches of 10,000 based on your current filters.
            </div>
            <button class="export-action-btn" @click="startExport()">
              <span class="export-action-btn-inner">
                <v-icon color="white" style="font-size:18px; margin-right:7px;"
                  >mdi-play-circle-outline</v-icon
                >
                Start Export
              </span>
            </button>
          </div>

          <!-- ── In-progress ── -->
          <div v-else-if="exportRunning">
            <div class="export-status-row mb-4">
              <v-icon color="success" class="pulse-icon mr-2"
                >mdi-cloud-download-outline</v-icon
              >
              <span class="export-status-text"
                >Fetching records from server…</span
              >
            </div>

            <div class="export-progress-wrap mb-1">
              <v-progress-linear
                :value="exportPercent"
                color="success"
                background-color="#e8f5e9"
                height="10"
                rounded
              ></v-progress-linear>
              <v-progress-linear
                indeterminate
                color="light-green lighten-2"
                height="4"
                class="export-shimmer-bar"
              ></v-progress-linear>
            </div>

            <div class="export-counts-row mt-3">
              <span class="export-count-fetched">{{
                exportFetched.toLocaleString()
              }}</span>
              <span class="export-count-sep"> / </span>
              <span class="export-count-total"
                >{{ exportTotal.toLocaleString() }} records</span
              >
              <v-spacer></v-spacer>
              <span class="export-percent-badge">{{ exportPercent }}%</span>
            </div>

            <div class="export-chunk-info mt-2">
              <v-icon
                small
                color="light-green darken-1"
                class="mr-1 export-spin-icon"
                style="vertical-align:middle"
                >mdi-autorenew</v-icon
              >
              <span class="export-batch-loading-text">
                Loading batch {{ exportCurrentChunk }} of
                {{ exportTotalChunks }}…
              </span>
            </div>
          </div>

          <!-- ── Cancelled ── -->
          <div v-else-if="exportCancelled" class="export-cancelled-state">
            <v-icon color="grey lighten-1" size="48" class="mb-3"
              >mdi-cancel</v-icon
            >
            <div class="export-final-text grey--text">Export cancelled.</div>
          </div>

          <!-- ── Done ── -->
          <div v-else class="export-done-state">
            <div class="export-done-check-wrap mb-3">
              <v-icon color="success" size="52">mdi-check-circle</v-icon>
            </div>
            <div class="export-final-text success--text mb-1">
              {{ exportFetched.toLocaleString() }} records ready!
            </div>
            <div class="export-done-subtitle grey--text mb-5">
              Click the button below to download your CSV file.
            </div>
            <button class="export-action-btn" @click="triggerDownload()">
              <span class="export-action-btn-inner">
                <v-icon color="white" style="font-size:18px; margin-right:7px;"
                  >mdi-download</v-icon
                >
                Download CSV
              </span>
            </button>
          </div>
        </v-card-text>

        <v-card-actions class="px-3 pb-4 pt-2">
          <v-spacer></v-spacer>
          <v-btn
            v-if="exportRunning"
            outline
            color="grey darken-1"
            @click="cancelExport()"
          >
            <v-icon left small>mdi-close</v-icon>
            Cancel
          </v-btn>
          <v-btn
            v-else
            outline
            color="grey darken-1"
            @click="closeExportDialog()"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import votingMasterListPerson from "./VotingMasterListPerson.vue";

export default {
  components: {
    votingMasterListPerson,
  },
  data: () => ({
    title: "DOE",
    processing: false,
    uploading: false,
    url: "",
    actions: [],

    importDialog: false,
    files: [],

    zipcodes: "",

    votingDialog: false,
    presonInfo: {},

    exportDialog: false,
    exportRunning: false,
    exportFetched: 0,
    exportTotal: 0,
    exportCurrentChunk: 0,
    exportTotalChunks: 0,
    exportCancelled: false,
    exportDone: false,
    exportBatchLoading: false,
    exportAbortController: null,
    exportReadyRecords: null,
    exportBaseUrl: "",
    exportToken: "",

    rules: {
      zipcode: (value) => {
        if (!value) return true;

        return value.length >= 5 || `Must be 5 digits`;
      },
    },

    columns: [
      {
        field: "isMatched",
        isIcon: true,
        template: "icon",
        alignment: "center",
        visible: true,
        allowFiltering: false,
        allowHeaderFiltering: false,
        allowHiding: false,
        allowSearch: false,
        cssClass: "width-25",
        headerFilter: {
          allowSearch: false,
          groupInterval: undefined,
          height: undefined,
          searchMode: "contains",
          width: undefined,
        },
        icon: (value, row) => {
          return value ? "mdi-check-circle" : "mdi-alert";
        },
        iconColor: (value, row) => {
          return value ? "success" : "#f35123";
        },
      },
      {
        field: "isMatched",
        label: "Matched",
        type: "boolean",
        format: "",
        visible: true,
        filterType: "",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        field: "regID",
        label: "DOE ID",
        format: "",
        visible: true,
        filterType: "",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        field: "first",
        label: "First",
        format: "",
        visible: true,
        filterType: "",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        field: "mi",
        label: "Middle Name",
        format: "",
        visible: false,
        filterType: "",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        field: "last",
        label: "Last",
        format: "",
        visible: true,
        filterType: "",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        field: "s",
        label: "Gander",
        format: "",
        visible: true,
        filterType: "",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        field: "personID",
        label: "Person ID",
        format: "",
        visible: true,
        filterType: "",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        field: "parentId",
        label: "Parent Id",
        format: "",
        visible: true,
        filterType: "",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        field: "house",
        label: "House",
        format: "",
        visible: true,
        filterType: "",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        field: "street",
        label: "Street",
        format: "",
        visible: true,
        filterType: "",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        field: "apt",
        label: "Apt",
        format: "",
        visible: true,
        filterType: "",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        field: "city",
        label: "City",
        format: "",
        visible: true,
        filterType: "",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        field: "zip",
        label: "Zip",
        format: "",
        visible: true,
        filterType: "",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        field: "tel",
        label: "Tel",
        format: "",
        visible: false,
        filterType: "",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        field: "matchID",
        label: "Match ID",
        format: "",
        visible: true,
        filterType: "",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        field: "matchName",
        label: "Matched To",
        format: "",
        visible: true,
        filterType: "",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        field: "matchType",
        label: "Matched Type",
        format: "",
        visible: true,
        filterType: "",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        field: "matchCount",
        label: "Match Type Count",
        format: "",
        visible: true,
        filterType: "",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        field: "dob",
        label: "DOB",
        format: "",
        visible: false,
        filterType: "",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        field: "party",
        label: "Party",
        format: "",
        visible: false,
        filterType: "",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        field: "council",
        label: "Council",
        format: "",
        visible: false,
        filterType: "",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        field: "dlv",
        label: "DLV",
        format: "",
        visible: false,
        filterType: "",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        field: "datE_REG",
        label: "Date Reg",
        format: "",
        visible: false,
        filterType: "",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        field: "election",
        label: "Election",
        format: "",
        visible: false,
        filterType: "",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        field: "assembly",
        label: "Assembly",
        format: "",
        visible: false,
        filterType: "",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        field: "congress",
        label: "Congress",
        format: "",
        visible: false,
        filterType: "",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        field: "senate",
        label: "Senate",
        format: "",
        visible: false,
        filterType: "",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        field: "civilCourtDistrict",
        label: "Civil Court District",
        format: "",
        visible: false,
        filterType: "",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        field: "judicialDistrict",
        label: "Judicial District",
        format: "",
        visible: false,
        filterType: "",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        field: "statusCode",
        label: "Status Code",
        format: "",
        visible: false,
        filterType: "",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        field: "voterType",
        label: "Voter Type",
        format: "",
        visible: false,
        filterType: "",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        field: "effStatusChangeDate",
        label: "Eff Status Change Date",
        format: "",
        visible: false,
        filterType: "",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        field: "isCurrent",
        label: "Is Current",
        format: "",
        visible: true,
        type: "boolean",
        filterType: "",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        field: "lastUpdated",
        label: "Last Updated",
        type: "date",
        format: "",
        visible: true,
        filterType: "",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        field: "updatedBy",
        label: "Updated By",
        format: "",
        visible: true,
        filterType: "",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        field: "voteHistory",
        label: "Voter History",
        format: "",
        visible: false,
        filterType: "",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
    ],
  }),
  created() {},
  computed: {
    exportPercent() {
      if (!this.exportTotal) return 0;
      return Math.min(
        100,
        Math.round((this.exportFetched / this.exportTotal) * 100)
      );
    },
  },
  methods: {
    beforeSetupGrid() {
      this.actions.push({
        text: "Import",
        action: this.openImportDialog,
      });

      this.actions.push({
        text: "Export",
        name: "Export",
        action: this.exportData,
      });

      this.url = "doe?isCurrent=false";
    },
    onRowClicked(row) {
      this.openVotingDialog(row);
    },
    async openVotingDialog(row) {
      if (row.personID == "No Ansl ID" || !row.personID) {
        return this.$notify({
          group: "notification",
          text: "No Ansl ID",
          type: "warning",
        });
      }

      this.presonInfo = await this.$http.get(
        "api/applicantStudents/getPersonByID?personID=" + row.personID
      );

      this.votingDialog = true;
    },
    closeVotingDialog() {
      this.votingDialog = false;
    },
    reload() {
      this.$refs.grid.reloadGrid();
    },
    openImportDialog() {
      this.importDialog = true;
    },
    pickFile() {
      this.$refs.files.click();
    },
    onFilePicked(e) {
      this.uploading = true;

      const files = e.target.files;

      if (!files) return;

      this.files = files;

      this.uploading = false;
    },
    closeImportDialog() {
      this.importDialog = false;
      this.files = [];
      this.$refs.files.value = "";
    },
    exportData() {
      const total = this.$refs.grid.getTotalCount();
      const chunkSize = 10000;

      this.exportToken = this.$store.state.authentication.token;
      this.exportBaseUrl = this.$refs.grid.buildExportUrl();
      this.exportFetched = 0;
      this.exportTotal = total || 0;
      this.exportCurrentChunk = 0;
      this.exportTotalChunks = total ? Math.ceil(total / chunkSize) : 0;
      this.exportCancelled = false;
      this.exportDone = false;
      this.exportRunning = false;
      this.exportBatchLoading = false;
      this.exportReadyRecords = null;
      this.exportAbortController = null;
      this.exportDialog = true;
    },
    async startExport() {
      const chunkSize = 10000;
      const maxRetries = 3;
      const retryDelayMs = 800;
      const batchDelayMs = 150;
      const baseUrl = this.exportBaseUrl;
      const token = this.exportToken;
      const separator = baseUrl.includes("?") ? "&" : "?";

      this.exportRunning = true;
      this.exportCancelled = false;
      this.exportDone = false;
      this.exportFetched = 0;
      this.exportCurrentChunk = 0;
      this.exportReadyRecords = null;
      this.exportAbortController = new AbortController();

      this.exportBatchLoading = true;
      this.exportCurrentChunk = 1;

      let skip = 0;
      let allRecords = [];
      let hasMore = true;

      try {
        while (hasMore) {
          if (this.exportCancelled) break;

          const url =
            baseUrl + separator + "$top=" + chunkSize + "&$skip=" + skip;

          let response = null;
          let lastError = null;

          for (var attempt = 1; attempt <= maxRetries; attempt++) {
            if (this.exportCancelled) break;
            try {
              response = await fetch(url, {
                headers: { Authorization: token },
                signal: this.exportAbortController.signal,
              });
              if (response.ok) {
                lastError = null;
                break;
              }
              lastError = new Error("HTTP " + response.status);
            } catch (fetchErr) {
              lastError = fetchErr;
              if (this.exportCancelled) break;
              if (attempt < maxRetries) {
                await new Promise(function(resolve) {
                  setTimeout(resolve, retryDelayMs * attempt);
                });
              }
            }
          }

          if (this.exportCancelled) break;
          if (lastError) throw lastError;

          this.exportBatchLoading = false;

          const data = await response.json();
          const records = data.value || [];

          allRecords = allRecords.concat(records);
          this.exportFetched = allRecords.length;

          if (!this.exportTotal && data["@odata.count"]) {
            this.exportTotal = data["@odata.count"];
            this.exportTotalChunks = Math.ceil(this.exportTotal / chunkSize);
          }

          skip += chunkSize;
          hasMore = records.length === chunkSize;

          if (hasMore && !this.exportCancelled) {
            this.exportCurrentChunk = Math.floor(skip / chunkSize) + 1;
            this.exportBatchLoading = true;
            await new Promise(function(resolve) {
              setTimeout(resolve, batchDelayMs);
            });
          }
        }

        if (!this.exportCancelled) {
          this.exportBatchLoading = false;
          this.exportRunning = false;
          this.exportReadyRecords = allRecords;
          this.exportDone = true;
        }
      } catch (e) {
        this.exportBatchLoading = false;
        this.exportRunning = false;

        if (this.exportCancelled) return;

        this.exportDialog = false;
        this.$notify({
          group: "notification",
          text:
            "Export failed: " + (e && e.message ? e.message : "Unknown error"),
          type: "error",
        });
      }
    },
    cancelExport() {
      this.exportCancelled = true;
      this.exportRunning = false;
      this.exportBatchLoading = false;
      if (this.exportAbortController) {
        this.exportAbortController.abort();
      }
    },
    triggerDownload() {
      this.downloadCSV(this.exportReadyRecords);
      this.exportDialog = false;
      this.exportReadyRecords = null;
    },
    closeExportDialog() {
      this.exportDialog = false;
      this.exportReadyRecords = null;
    },
    downloadCSV(records) {
      if (!records || !records.length) {
        this.$notify({
          group: "notification",
          text: "No records to export",
          type: "warning",
        });
        return;
      }

      const exportColumns = this.columns.filter(function(c) {
        return !c.isIcon && c.label;
      });

      const escapeCSV = function(value) {
        if (value === null || value === undefined) return "";
        const str = String(value);
        if (
          str.indexOf(",") !== -1 ||
          str.indexOf('"') !== -1 ||
          str.indexOf("\n") !== -1
        ) {
          return '"' + str.replace(/"/g, '""') + '"';
        }
        return str;
      };

      const header = exportColumns
        .map(function(c) {
          return escapeCSV(c.label);
        })
        .join(",");

      const rows = records.map(function(record) {
        return exportColumns
          .map(function(col) {
            return escapeCSV(record[col.field]);
          })
          .join(",");
      });

      const csv = [header].concat(rows).join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.setAttribute("download", "DOE.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
    },
    async importFile() {
      if (!this.$refs.zipcodes.validate(true)) return;

      try {
        this.processing = true;

        var formData = new FormData();

        formData.append("upload_file", this.files[0]);

        const response = await this.$http.post(
          "api/doe/Import?user=" +
            this.$store.state.authentication.user +
            "&zipcodes=" +
            this.zipcodes,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );

        this.$notify({
          group: "notification",
          duration: 10000,
          text: response.newRecords + " New Records",
          // +
          // response.updatedRecords +
          // " Updated Records",
          type: "success",
        });

        this.processing = false;
        this.importDialog = false;
        this.files = [];
        this.$refs.files.value = "";
        this.zipcodes = "";

        this.$refs.grid.reloadGrid();
      } catch (e) {
        this.processing = false;
      }
    },
  },
};
</script>
<style scoped>
.export-dialog-card {
  border-radius: 10px;
  overflow: hidden;
}

.export-dialog-header {
  background: linear-gradient(135deg, #4caf50 0%, #388e3c 100%);
  padding: 20px 24px;
  display: flex;
  align-items: center;
}

.export-dialog-title {
  color: #fff;
  font-size: 18px;
  font-weight: 500;
  letter-spacing: 0.3px;
}

.export-status-row {
  display: flex;
  align-items: center;
}

.export-status-text {
  font-size: 14px;
  color: #555;
}

/* Idle state */
.export-idle-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 8px 0 4px;
}

.export-idle-icon-wrap {
  background: #c8e6c9;
  border-radius: 50%;
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.export-idle-title {
  font-size: 17px;
  font-weight: 600;
  color: #333;
}

.export-idle-subtitle {
  font-size: 13px;
  text-align: center;
  line-height: 1.5;
  max-width: 320px;
}

/* Progress bar container — shimmer sits flush below the main bar */
.export-progress-wrap {
  position: relative;
}

.export-shimmer-bar {
  margin-top: 3px !important;
  border-radius: 4px;
  opacity: 0.85;
}

.export-counts-row {
  display: flex;
  align-items: baseline;
}

.export-count-fetched {
  font-size: 20px;
  font-weight: 600;
  color: #2e7d32;
}

.export-count-sep {
  font-size: 16px;
  color: #aaa;
  margin: 0 2px;
}

.export-count-total {
  font-size: 14px;
  color: #777;
}

.export-percent-badge {
  font-size: 13px;
  font-weight: 600;
  color: #fff;
  background: #4caf50;
  border-radius: 12px;
  padding: 2px 10px;
}

.export-chunk-info {
  font-size: 12px;
  color: #aaa;
  min-height: 20px;
}

.export-batch-loading-text {
  color: #558b2f;
  font-weight: 500;
}

/* Done state */
.export-done-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 12px 0 4px;
}

.export-done-check-wrap {
  background: #e8f5e9;
  border-radius: 50%;
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.export-done-subtitle {
  font-size: 13px;
  text-align: center;
}

.export-action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #4caf50 0%, #388e3c 100%);
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 11px 28px;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0.4px;
  cursor: pointer;
  box-shadow: 0 3px 8px rgba(76, 175, 80, 0.4);
  transition: box-shadow 0.2s, transform 0.15s, filter 0.15s;
}

.export-action-btn:hover {
  filter: brightness(1.07);
  box-shadow: 0 5px 14px rgba(76, 175, 80, 0.5);
  transform: translateY(-1px);
}

.export-action-btn:active {
  filter: brightness(0.95);
  transform: translateY(0);
  box-shadow: 0 2px 5px rgba(76, 175, 80, 0.35);
}

.export-action-btn-inner {
  display: flex;
  align-items: center;
}

/* Cancelled state */
.export-cancelled-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px 0 8px;
}

.export-final-text {
  font-size: 15px;
  font-weight: 500;
  text-align: center;
}

/* Animations */
.pulse-icon {
  animation: pulse 1.4s ease-in-out infinite;
}

@keyframes pulse {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.4;
  }
  100% {
    opacity: 1;
  }
}

.export-spin-icon {
  animation: spin 1s linear infinite;
  display: inline-block;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
